import { useBasePath } from '@ais-ui/custom-hooks';
import { useCallback, useMemo } from 'react';

import {
  Configuration,
  ConfigurationApi,
  GetStations200Response,
  GroupDefinition,
  ServiceDefinition,
  type UserMmsiGet200Response,
} from '@ais-ui/api-spec/config';
import { useSession } from '@ais-ui/websockets';

import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';

const useAISConfigApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const configApi = useMemo(
    () => new ConfigurationApi(configuration),
    [configuration]
  );
  return configApi;
};

// export const useAccessRights = () => {
//   const configApi = useAISConfigApi();
//
//   return useQuery<GetAccessRights200Response, Error>(
//     ['accessRights'],
//     async () => {
//       const response = await configApi.getAccessRights();
//       return response;
//     }
//   );
// };

export const useGroupUidGet = (uid: string) => {
  const configApi = useAISConfigApi();

  return useQuery<GroupDefinition, Error>(['groupUidGet'], async () => {
    const response = await configApi.groupUidGet({ uid: uid });
    return response;
  });
};

export const useGetUserMmsi = () => {
  const configApi = useAISConfigApi();

  return useQuery<UserMmsiGet200Response, Error>(['userMmsi'], async () => {
    const response = await configApi.userMmsiGet({ dc: 1 });
    return response;
  });
};

export const useGetStations = () => {
  const configApi = useAISConfigApi();

  return useQuery<GetStations200Response, Error>(['stations'], async () => {
    const response = await configApi.getStations();
    return response;
  });
};

// Hook for getting all service definitions
export const useGetServiceDefinitions = () => {
  const configApi = useAISConfigApi();

  return useQuery<ServiceDefinition, Error>(
    ['serviceDefinitions'],
    async () => {
      const response = await configApi.getServiceDefinitions();
      return response;
    }
  );
};
